import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { addParameterToUrl, getUrlParameter, replaceClientDataUrlRouteQueryVariables } from '@portal-internet/core';
import { useData } from 'hooks/useData';
import { useViewport } from 'hooks/corporatiu/useViewport';
import Boto from 'components/corporatiu/Boto';
import Titol from 'components/corporatiu/Titol';
import DestacatBasic from 'components/corporatiu/Destacat/DestacatBasic/DestacatBasic';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './mosaic.module.scss';

const TitolMosaic = (props) => {
    const { titol, numItems } = props;
    const showTitle = numItems > 0;
    if (showTitle && !__isEmpty(titol)) {
        return <Titol titol={titol} tag='h2' />;
    }
    return <></>;
};

const getCurrentUrlPag = (url) => {
    return parseInt(getUrlParameter(url, 'pagina')) || 1;
};

const getUrlPage = (url, page) => {
    return addParameterToUrl(url, 'pagina', page);
};

export const MoreItemsButton = (props) => {
    const {
        mostrarBotoMesItems,
        urlCarregaDinamica,
        routerQuery,
        limitCarregaDinamica,
        setItemsDinamics,
        numItems,
        totalPagines,
        className
    } = props;
    const { data: itemsDinamic, paginacio, getDades } = useData();
    const url = replaceClientDataUrlRouteQueryVariables(urlCarregaDinamica, routerQuery);
    const [numPagLoadQuery, setNumPagLoadQuery] = useState(getCurrentUrlPag(url) + 1);
    const [showButtonDinamic, setShowButtonDinamic] = useState(numItems > 0 && totalPagines > 1);
    const [numPagesLoaded, setNumPagesLoaded] = useState(0);

    const loadMoreItems = () => {
        setNumPagesLoaded(numPagesLoaded + 1);
        getDades(getUrlPage(url, numPagLoadQuery));
    };

    useEffect(() => {
        if (!__isEmpty(itemsDinamic)) {
            const tieneInfoContingut =
                Array.isArray(itemsDinamic) && itemsDinamic.every((item) => item.hasOwnProperty('info_contingut'));
            if (tieneInfoContingut) {
                setItemsDinamics(itemsDinamic.map((item) => item.info_contingut));
            } else {
                setItemsDinamics(itemsDinamic);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsDinamic]);

    useEffect(() => {
        if (limitCarregaDinamica && numPagesLoaded >= limitCarregaDinamica) setShowButtonDinamic(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numPagesLoaded]);

    useEffect(() => {
        if (!__isEmpty(paginacio) && paginacio.total_pagines) {
            if (paginacio.pagina_actual === paginacio.total_pagines) {
                setShowButtonDinamic(false);
            } else if (paginacio.pagina_actual < paginacio.total_pagines) {
                setNumPagLoadQuery(paginacio.pagina_actual + 1);
            }
        }
    }, [paginacio]);

    // Resincronitza l'estat en canviar de tab, ja que Structure.jsx reutilitza la instància del component sense remuntar-la
    useEffect(() => {
        setShowButtonDinamic(numItems > 0 && totalPagines > 1);
        setNumPagLoadQuery(getCurrentUrlPag(url) + 1);
        setNumPagesLoaded(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    const stylesBtn = clsx({
        [styles.btnMoreItemsContent]: true,
        [className]: !!className
    });

    return mostrarBotoMesItems && urlCarregaDinamica && showButtonDinamic ? (
        <div className={stylesBtn}>
            <Boto
                tag='button'
                text="Mostra'n més"
                className={styles.btn}
                estil='transparent'
                icona='fletxa_simple_avall'
                mida='s'
                onClick={loadMoreItems}
            />
        </div>
    ) : (
        <></>
    );
};

const Mosaic = (props) => {
    const {
        numColumnsMobile,
        className,
        estilMosaic,
        urlCarregaDinamica,
        totalPagines,
        mostraRanking,
        routePath,
        hGap = 200,
        vGap = 200,
        numColumns = false,
        elementsAMostrar = false,
        mostrarBotoMesItems = false,
        mostrarData = false,
        mostrarId = false,
        numColumnsRow = 12,
        tipusImatge = 'web_destacat',
        icona = 'auto'
    } = props;

    const router = useRouter();
    const { isOnlyMobile } = useViewport();

    const [itemsDinamics, setItemsDinamics] = useState([]);
    const [idNewItem, setIdNewItem] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [toggleMessage, setToggleMessage] = useState(true);
    const [items, setItems] = useState(props.children);

    const columns = numColumnsMobile && isOnlyMobile ? numColumnsMobile : numColumns;
    const columnsInt = columns ? Math.floor(columns) : 1;
    const colWidth = columns ? Math.round(numColumnsRow / columnsInt) : numColumnsRow;

    useEffect(() => {
        setItemsDinamics([]);
        // Scroll to hash if present in URL
        if (typeof window !== 'undefined') {
            const hash = router.asPath.split('#')[1];
            if (hash) {
                const timeoutId = setTimeout(() => {
                    const element = document.getElementById(hash);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);

                return () => clearTimeout(timeoutId);
            }
        }
    }, [router.asPath]);

    useEffect(() => {
        const allItems = props.children || props.items || [];
        setItems([...allItems, ...itemsDinamics]);
    }, [props.children, props.items, itemsDinamics]);

    if (!items) return <></>;

    const stylesContainerMosaic = clsx({
        [styles.contenidorMosaic]: true
    });

    const stylesMosaic = clsx({
        [styles.mosaic]: true,
        [className]: !!className,
        [styles.ranking]: mostraRanking,
        [styles[estilMosaic]]: !!estilMosaic,
        [styles[`column-gap-${hGap}`]]: !!hGap,
        [styles[`row-gap-${vGap}`]]: !!vGap,
        [styles[`columns-${columns}`]]: !className && !!columns
    });

    const itemsToRender = () => {
        return !__isEmpty(items)
            ? items.slice(0, elementsAMostrar || items.length).map((component, i) => {
                  if (React.isValidElement(component)) {
                      return React.cloneElement(component, {
                          key: component.key || `react-${i}`,
                          numColumnsRow: colWidth
                      });
                  } else {
                      return (
                          <DestacatBasic
                              key={`destacat-premsa-${i}`}
                              item={component}
                              routePath={routePath}
                              mostrarData={mostrarData}
                              mostrarId={mostrarId}
                              mostrarImatge='true'
                              numColumnsRow={colWidth}
                              tipusImatge={tipusImatge}
                              icona={icona}
                          />
                      );
                  }
              })
            : null;
    };

    return (
        <div data-testid='mosaic' className={stylesContainerMosaic}>
            <TitolMosaic {...props} numItems={items.length} />
            <div className={`${stylesMosaic} mosaic`}>{itemsToRender()}</div>
            <MoreItemsButton
                {...props}
                numItems={items.length}
                setItemsDinamics={(newItems) => {
                    setAnnouncement(toggleMessage ? "S'ha mostrat més contingut" : "S'ha carregat més contingut");
                    setToggleMessage(!toggleMessage);
                    setTimeout(() => {
                        setIdNewItem(newItems?.[0]?.id);
                        setItemsDinamics([...itemsDinamics, ...newItems]);
                        setAnnouncement('');
                    }, 100);
                }}
                mostrarBotoMesItems={mostrarBotoMesItems}
                urlCarregaDinamica={urlCarregaDinamica}
                totalPagines={totalPagines}
            />
            <div aria-live='assertive' className={styles.announcement} data-load={idNewItem}>
                {announcement}
            </div>
        </div>
    );
};

export default Mosaic;
